/**
 * Seats down from the bridge's work queue (Bug 34685): a seat whose process
 * exited is invisible to the context snapshots, so the wallboard reads the
 * bridge's liveness verdict instead. Tim, 9/28: "The Wallboard definitely
 * should show when seats are down."
 *
 * A row is DOWN only when every arm agrees (the ticket's AC 2):
 * - the bridge band is `dead` (no running process, no recent model call), AND
 * - the seat's bridge heartbeat (`lastSeen`) is older than the liveness
 *   window. The band alone is not enough: on 9/28 thirteen seats that were
 *   DMing read `dead` (a lagging model-call stamp), yet all of them were still
 *   heartbeating, AND
 * - it was alive within the last day. An unretired seat that has been dark for
 *   days is abandoned, not an outage, and a wall of those would bury the one
 *   that just fell over.
 *
 * Only a row the bridge calls a seat counts (AC 3): `isLivenessSeat`, the
 * same `OutageDetector.IsLivenessSeat` rule the outage detector uses, so
 * helpers (Layer `agent`), the home flock, services, collaborators, Roost UI
 * clients, probes and ephemeral rows never land on Seats down. The client
 * never re-derives a copy of that rule. An expected helper that is not
 * running shows under the daemons rows instead. A row without the verdict (a
 * SQL-only orphan, or a bridge older than the field) is never called down:
 * unknown is not a verdict.
 */
import { parseUtcMs } from '../models/flock-telemetry.utils';
import type { SeatWorkQueue } from '../models/work-queue.model';
import type { Alert } from './attention.logic';
import { formatDuration } from './wallboard.logic';

/** The bridge's liveness window: a heartbeat older than this is silence. */
export const SEAT_DOWN_SILENT_MS = 15 * 60_000;
/** Dark longer than this is an abandoned seat, not a seat that went down. */
export const SEAT_DOWN_RECENT_MS = 24 * 60 * 60_000;

/** True only when the bridge says the row is a seat; absent fails closed. */
export function isFlockSeat(
  row: Pick<SeatWorkQueue, 'isLivenessSeat'>
): boolean {
  return row.isLivenessSeat === true;
}

/**
 * Crit "Seats down" rows. `observedIds` are seats a fresh context snapshot or
 * the Control Center still sees, so they are running and already have rows.
 */
export function deadSeatAlerts(
  rows: readonly SeatWorkQueue[],
  observedIds: ReadonlySet<string>,
  nowMs: number
): Alert[] {
  const out: Alert[] = [];
  for (const r of rows) {
    if (r.retired || r.livenessBand !== 'dead' || !isFlockSeat(r)) continue;
    if (observedIds.has(r.instanceId)) continue;
    const seenMs = parseUtcMs(r.lastSeen);
    const callMs = parseUtcMs(r.lastModelCallAt);
    if (seenMs === null) continue;
    if (nowMs - seenMs <= SEAT_DOWN_SILENT_MS) continue;
    const lastLifeMs = Math.max(seenMs, callMs ?? 0);
    const darkMs = nowMs - lastLifeMs;
    if (darkMs > SEAT_DOWN_RECENT_MS) continue;
    const call =
      callMs === null
        ? 'no model call on record'
        : `last model call ${formatDuration(nowMs - callMs)} ago`;
    out.push(
      seatDownAlert(
        r.instanceId,
        `no process or bridge contact for ${formatDuration(nowMs - seenMs)} · ${call}`
      )
    );
  }
  return out;
}

/** One crit Seats down row; shared with the desired-roster rule (WI 34807). */
export function seatDownAlert(instanceId: string, detail: string): Alert {
  return {
    id: `seat|${instanceId}|down`,
    severity: 'crit',
    kind: 'seat',
    category: 'down',
    title: `${instanceId} is down`,
    detail,
    action: { type: 'open-seat', label: 'Open seat', target: instanceId },
    // Below a stuck prompt (900+) and a logged-out seat (1000), which the
    // Control Center can act on directly; above a silent machine (800).
    weight: 850,
  };
}
